import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useInstitutionLogos } from '@/hooks/use-institution-logos';
import { filterActions, filterStore } from '@/state/transactions-filter-state';
import { cn } from '@/components/ui/utils';
import { ArrowUpDown, Search, Settings2, X } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import {
  AllAccountsCard,
  BankAccountFilterCard,
} from '@/components/accounts/BankAccountFilterCard';
import { TransactionFiltersDialog } from './TransactionFiltersDialog';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ActiveFiltersChips } from '@/components/transactions/ActiveFiltersChips';

// Clear button component for active filters
const FilterClearButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) => (
  <button
    onClick={onClick}
    className='h-9 px-2 border border-l-0 border-primary rounded-r-md bg-red-50 text-red-600 hover:bg-red-100 text-xs transition-colors'
  >
    <X className='w-3 h-3' />
  </button>
);

export const InlineFiltersBar = memo(function InlineFiltersBar() {
  const filterSnap = useSnapshot(filterStore);
  const { data: bankAccountsData } = useBankAccounts();
  const { getLogoForAccount } = useInstitutionLogos(bankAccountsData.rows);

  // Get selected account IDs
  const selectedAccountIds = useMemo(() => {
    return (filterSnap.appliedFilters.accountFilter || []) as string[];
  }, [filterSnap.appliedFilters.accountFilter]);

  // Check if all accounts are selected
  const isAllAccountsSelected = selectedAccountIds.length === 0;

  // Check if date filter is active
  const isDateFilterActive =
    !!filterSnap.appliedFilters.dateRange?.from ||
    !!filterSnap.appliedFilters.dateRange?.to;

  // Toggle account selection (immediate apply)
  const toggleAccountSelection = useCallback(
    (accountId: string) => {
      const currentSelection = [...selectedAccountIds];
      const index = currentSelection.indexOf(accountId);

      if (index > -1) {
        currentSelection.splice(index, 1);
      } else {
        currentSelection.push(accountId);
      }

      filterActions.updateFilter(
        'accountFilter',
        currentSelection.length > 0 ? currentSelection : undefined,
        true
      );
    },
    [selectedAccountIds]
  );

  // Select all accounts
  const selectAllAccounts = useCallback(() => {
    filterActions.updateFilter('accountFilter', undefined, true);
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (range: { from?: Date; to?: Date } | undefined) => {
      filterActions.updateFilter('dateRange', range, true);
    },
    []
  );

  // Handle search input with immediate apply
  const handleSearchChange = useCallback((value: string) => {
    filterActions.updateSearchQuery(value);
  }, []);

  // Count active advanced filters (for badge on "More" button)
  const advancedFiltersCount = useMemo(() => {
    let count = 0;
    if (
      filterSnap.appliedFilters.amountRange?.min ||
      filterSnap.appliedFilters.amountRange?.max
    ) {
      count++;
    }
    // if (
    //   filterSnap.appliedFilters.status &&
    //   filterSnap.appliedFilters.status.length > 0
    // ) {
    //   count++;
    // }
    return count;
  }, [filterSnap.appliedFilters]);

  // Clear handlers for each filter
  const clearDateFilter = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    filterActions.updateFilter('dateRange', undefined, true);
  }, []);

  const clearAdvancedFilters = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    filterActions.updateFilter('amountRange', undefined, true);
    // filterActions.updateFilter('status', undefined, true);
  }, []);

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      filterActions.updateSort(value as typeof filterSnap.sortBy);
    },
    [filterSnap]
  );

  return (
    <div className='space-y-3 bg-background rounded-lg border p-4'>
      {/* Row 1: Bank Accounts + Sort */}
      <div className='flex items-start justify-between gap-4 mb-1'>
        <div className='flex flex-wrap gap-1.5'>
          <AllAccountsCard
            selected={isAllAccountsSelected}
            onToggle={selectAllAccounts}
            compact
          />
          {bankAccountsData.rows.map((account) => (
            <BankAccountFilterCard
              key={account.$id}
              accountName={account.account_name}
              logoUrl={getLogoForAccount(account)}
              selected={selectedAccountIds.includes(account.$id!)}
              onToggle={() => toggleAccountSelection(account.$id!)}
              compact
            />
          ))}
        </div>

        {/* Sort */}
        <Select value={filterSnap.sortBy} onValueChange={handleSortChange}>
          <SelectTrigger className='w-[160px] h-8 shrink-0'>
            <ArrowUpDown className='w-3.5 h-3.5 mr-2 text-muted-foreground' />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='date-desc'>Newest first</SelectItem>
            <SelectItem value='date-asc'>Oldest first</SelectItem>
            <SelectItem value='amount-desc'>Highest amount</SelectItem>
            <SelectItem value='amount-asc'>Lowest amount</SelectItem>
            <SelectItem value='description-asc'>A → Z</SelectItem>
            <SelectItem value='description-desc'>Z → A</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Row 2: Search and Quick Filters */}
      <div className='flex flex-wrap items-center gap-3'>
        {/* Search Input */}
        <div className='relative shrink-0 min-w-[300px]'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none' />
          <Input
            placeholder='Search descriptions...'
            value={filterSnap.appliedFilters.searchQuery || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className='pl-10 h-9'
          />
          {filterSnap.appliedFilters.searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              className='absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
            >
              <X className='w-4 h-4' />
            </button>
          )}
        </div>

        {/* Date Range Picker */}
        <div className='flex items-center'>
          <DateRangePicker
            value={filterSnap.appliedFilters.dateRange}
            onChange={handleDateRangeChange}
            isActive={isDateFilterActive}
          />
          {isDateFilterActive && (
            <FilterClearButton onClick={clearDateFilter} />
          )}
        </div>
        {/* Transaction Type */}
        {/* <div className='flex items-center'>
          <Select value={currentTypeValue} onValueChange={handleTypeChange}>
            <SelectTrigger
              className={cn(
                'w-[130px] h-9',
                currentTypeValue !== 'all' && 'border-primary rounded-r-none border-r-0'
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All types</SelectItem>
              <SelectItem value='income'>Income</SelectItem>
              <SelectItem value='expense'>Expenses</SelectItem>
            </SelectContent>
          </Select>
          {currentTypeValue !== 'all' && (
            <FilterClearButton onClick={clearTypeFilter} />
          )}
        </div> */}

        {/* Advanced Filters Button */}
        <div className='flex items-center'>
          <TransactionFiltersDialog>
            <Button
              variant='outline'
              size='sm'
              className={cn(
                'h-9 gap-2',
                advancedFiltersCount > 0 &&
                  'border-primary rounded-r-none border-r-0'
              )}
            >
              <Settings2 className='w-4 h-4' />
              More filters
            </Button>
          </TransactionFiltersDialog>
          {advancedFiltersCount > 0 && (
            <FilterClearButton onClick={clearAdvancedFilters} />
          )}
        </div>
      </div>

      {/* Row 3: Active Filters */}
      <ActiveFiltersChips />
    </div>
  );
});
